import { useMemo, useState } from "react";
import { Input } from "./ui/input";
import { ArrowUpDown, ChevronUp, ChevronDown, Search } from "lucide-react";

export function FunnelList({ items, columns, onRowClick, testid = "funnel-list", rowAccent }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      Object.values(it).some(
        (v) => (typeof v === "string" || typeof v === "number") && String(v).toLowerCase().includes(q)
      )
    );
  }, [items, query]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
    return arr;
  }, [filtered, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid={`${testid}-search`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="h-9 pl-9"
          />
        </div>
        {query && <span className="text-xs text-slate-400">{sorted.length} match{sorted.length === 1 ? "" : "es"}</span>}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testid}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-100">
                <th className="py-3 px-4 w-12">#</th>
                {columns.map((c) => (
                  <th key={c.label} className="py-3 px-4 whitespace-nowrap">
                    {c.sortKey ? (
                      <button
                        data-testid={`sort-${c.sortKey}`}
                        onClick={() => toggleSort(c.sortKey)}
                        className="flex items-center gap-1 uppercase tracking-wide hover:text-ccc-navy transition-colors"
                      >
                        {c.label}
                        {sort.key === c.sortKey
                          ? (sort.dir === 1 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)
                          : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                      </button>
                    ) : c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((it, idx) => (
                <tr
                  key={it._id}
                  data-testid={`list-row-${it._id}`}
                  onClick={() => onRowClick(it)}
                  style={rowAccent ? { boxShadow: `inset 4px 0 0 0 ${rowAccent(it)}` } : undefined}
                  className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                >
                  <td className="py-3 px-4 align-middle text-slate-400 font-mono text-xs">{idx + 1}</td>
                  {columns.map((c) => <td key={c.label} className="py-3 px-4 align-middle">{c.render(it)}</td>)}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="py-10 text-center text-sm text-slate-400">{items.length === 0 ? "No records yet." : "No matches."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ViewToggle({ view, setView }) {
  const btn = (v) =>
    `px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${view === v ? "bg-ccc-navy text-white" : "text-slate-500 hover:text-ccc-navy"}`;
  return (
    <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1" data-testid="view-toggle">
      <button data-testid="view-list" className={btn("list")} onClick={() => setView("list")}>List</button>
      <button data-testid="view-board" className={btn("board")} onClick={() => setView("board")}>Board</button>
    </div>
  );
}
